import { fetchAuthSession } from '@aws-amplify/auth';

import env from '../config/env';
import { BACKEND_SEMANTIC_MODEL_API } from '../env';

const API_BASE = BACKEND_SEMANTIC_MODEL_API;
const BASE_URL = `${API_BASE}/semantic-tables`;

const SEMANTIC_TABLE_API_KEY =
  env?.API_KEY ||
  (typeof process !== 'undefined' ? process.env.REACT_APP_SEMANTIC_MODEL_API_KEY : undefined) ||
  (typeof process !== 'undefined' ? process.env.REACT_APP_X_API_KEY : undefined);

const defaultHeaders = {
  Accept: 'application/json',
  ...(SEMANTIC_TABLE_API_KEY ? { 'x-api-key': SEMANTIC_TABLE_API_KEY } : {})
};

if (!SEMANTIC_TABLE_API_KEY) {
  // eslint-disable-next-line no-console
  console.warn(
    'SemanticTableService: no se detectó API key (REACT_APP_SEMANTIC_MODEL_API_KEY o REACT_APP_X_API_KEY). ' +
      'Las llamadas al backend podrían responder 401/CORS.'
  );
}

function coalesce(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
}

function mapSoftDeletePayload(value) {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'boolean') {
    return { deleted: value };
  }

  if (typeof value !== 'object') {
    return undefined;
  }

  const deleted = coalesce(value.deleted, value.DELETED, value.is_deleted, value.IS_DELETED);
  const deletedAt = coalesce(value.deleted_at, value.DELETED_AT);

  const payload = {};

  if (deleted !== undefined) {
    payload.deleted = !!deleted;
  }
  if (deletedAt !== undefined) {
    payload.deleted_at = deletedAt;
  }
  if (payload.deleted && payload.deleted_at === undefined) {
    payload.deleted_at = null;
  }

  return Object.keys(payload).length ? payload : undefined;
}

function mapAuditPayload(value) {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const audit = {};
  ['created_by', 'created_at', 'updated_by', 'updated_at'].forEach((key) => {
    const aliasUpper = key.toUpperCase();
    const val = coalesce(value[key], value[aliasUpper]);
    if (val !== undefined) {
      audit[key] = val;
    }
  });

  return Object.keys(audit).length ? audit : undefined;
}

function mapTags(value) {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value.map((tag) => String(tag)).filter((tag) => tag.trim() !== '');
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return undefined;
}

function mapBoolean(value) {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'n'].includes(normalized)) {
      return false;
    }
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return !!value;
}

function normalizeColumn(column = {}) {
  if (!column || typeof column !== 'object') {
    return null;
  }

  const columnName = coalesce(column.column_name, column.COLUMN_NAME, column.name);

  if (!columnName || String(columnName).trim() === '') {
    return null;
  }

  const normalized = {
    column_name: String(columnName).trim()
  };

  const columnId = coalesce(column.column_id, column.columns_id, column.COLUMN_ID, column.COLUMNS_ID, column.id);
  if (columnId) {
    normalized.column_id = String(columnId);
  }

  const businessName = coalesce(column.business_name, column.BUSINESS_NAME);
  if (businessName !== undefined) {
    normalized.business_name = String(businessName);
  }

  const description = coalesce(column.description, column.DESCRIPTION);
  if (description !== undefined) {
    normalized.description = String(description);
  }

  const dataType = coalesce(column.data_type, column.DATA_TYPE);
  if (dataType !== undefined) {
    normalized.data_type = String(dataType);
  }

  const semanticRole = coalesce(column.semantic_role, column.SEMANTIC_ROLE);
  if (semanticRole !== undefined) {
    normalized.semantic_role = String(semanticRole);
  }

  const semanticType = coalesce(column.semantic_type, column.SEMANTIC_TYPE);
  if (semanticType !== undefined) {
    normalized.semantic_type = String(semanticType);
  }

  const usageNotes = coalesce(column.usage_notes, column.USAGE_NOTES);
  if (usageNotes !== undefined) {
    normalized.usage_notes = String(usageNotes);
  }

  const businessRules = coalesce(column.business_rules, column.BUSINESS_RULES);
  if (businessRules !== undefined) {
    normalized.business_rules = String(businessRules);
  }

  const flags = [
    'is_primary_key',
    'nullable',
    'required',
    'sensitive'
  ];

  flags.forEach((flag) => {
    const aliasUpper = flag.toUpperCase();
    const aliasCamel = flag.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    const value = coalesce(column[flag], column[aliasUpper], column[aliasCamel]);
    const boolValue = mapBoolean(value);
    if (boolValue !== undefined) {
      normalized[flag] = boolValue;
    }
  });

  return normalized;
}

function normalizeColumns(columns) {
  if (!Array.isArray(columns)) {
    return [];
  }

  return columns
    .map(normalizeColumn)
    .filter(Boolean);
}

function buildSemanticTablePayload(source = {}) {
  if (source === null || typeof source !== 'object') {
    throw new Error('SemanticTableService: payload debe ser un objeto');
  }

  const accountId = coalesce(source.account_id, source.accountId, source.ACCOUNT_ID, source.ACCOUNTID);
  if (!accountId) {
    throw new Error('SemanticTableService: account_id es requerido');
  }

  const semanticDatabaseId = coalesce(
    source.semantic_database_id,
    source.semanticDatabaseId,
    source.SEMANTIC_DATABASE_ID
  );
  if (!semanticDatabaseId) {
    throw new Error('SemanticTableService: semantic_database_id es requerido');
  }

  const connectionId = coalesce(
    source.connection_id,
    source.connectionId,
    source.CONNECTION_ID,
    source.CONNECTIONID
  );
  if (!connectionId) {
    throw new Error('SemanticTableService: connection_id es requerido');
  }

  const databaseName = coalesce(source.database, source.DATABASE, source.database_name, source.DATABASE_NAME);
  if (!databaseName) {
    throw new Error('SemanticTableService: database es requerido');
  }

  const schemaName = coalesce(source.schema, source.SCHEMA, source.schema_name, source.SCHEMA_NAME);
  if (!schemaName) {
    throw new Error('SemanticTableService: schema es requerido');
  }

  const tableName = coalesce(source.table_name, source.tableName, source.TABLE_NAME);
  if (!tableName) {
    throw new Error('SemanticTableService: table_name es requerido');
  }

  const columns = normalizeColumns(
    coalesce(source.columns, source.COLUMNS)
  );

  if (!columns.length) {
    throw new Error('SemanticTableService: columns debe ser una lista no vacía');
  }

  const payload = {
    account_id: String(accountId),
    semantic_database_id: String(semanticDatabaseId),
    connection_id: String(connectionId),
    database: String(databaseName),
    schema: String(schemaName),
    table_name: String(tableName),
    columns
  };

  const tableId = coalesce(source.table_id, source.tableId, source.TABLE_ID, source.id);
  if (tableId) {
    payload.table_id = String(tableId);
  }

  const catalog = coalesce(source.catalog, source.CATALOG);
  if (catalog !== undefined) {
    payload.catalog = String(catalog);
  }

  const displayName = coalesce(source.display_name, source.displayName, source.DISPLAY_NAME);
  if (displayName !== undefined) {
    payload.display_name = String(displayName);
  }

  const description = coalesce(source.description, source.DESCRIPTION);
  if (description !== undefined) {
    payload.description = String(description);
  }

  const sqlDialect = coalesce(source.sql_dialect, source.sqlDialect, source.SQL_DIALECT);
  if (sqlDialect !== undefined) {
    payload.sql_dialect = String(sqlDialect);
  }

  const tags = mapTags(coalesce(source.tags, source.TAGS));
  if (tags && tags.length) {
    payload.tags = tags;
  }

  const isActive = coalesce(source.is_active, source.IS_ACTIVE);
  if (isActive !== undefined) {
    payload.is_active = mapBoolean(isActive);
  }

  const version = coalesce(source.version, source.VERSION);
  if (version !== undefined) {
    payload.version = String(version);
  }

  const softDelete = mapSoftDeletePayload(coalesce(source.soft_delete, source.softDelete, source.SOFT_DELETE));
  if (softDelete) {
    payload.soft_delete = softDelete;
  }

  const audit = mapAuditPayload(coalesce(source.audit, source.AUDIT));
  if (audit) {
    payload.audit = audit;
  }

  const joins = coalesce(source.joins, source.JOINS);
  if (Array.isArray(joins) && joins.length) {
    payload.joins = joins;
  }

  const lineage = coalesce(source.lineage, source.LINEAGE);
  if (lineage && typeof lineage === 'object') {
    payload.lineage = lineage;
  }

  const governance = coalesce(source.governance, source.GOVERNANCE);
  if (governance && typeof governance === 'object') {
    payload.governance = governance;
  }

  const qualityRules = coalesce(source.quality_rules, source.qualityRules, source.QUALITY_RULES);
  if (Array.isArray(qualityRules) && qualityRules.length) {
    payload.quality_rules = qualityRules;
  }

  return payload;
}

function decorateColumn(column) {
  if (!column || typeof column !== 'object') {
    return column;
  }

  const decorated = { ...column };

  const assign = (canonical, aliases = [], transform) => {
    const value = coalesce(decorated[canonical], ...aliases.map((alias) => decorated[alias]));
    if (value === undefined) {
      return;
    }
    const finalValue = transform ? transform(value) : value;
    decorated[canonical] = finalValue;
    aliases.forEach((alias) => {
      decorated[alias] = finalValue;
    });
  };

  assign('column_id', ['columns_id', 'COLUMN_ID', 'COLUMNS_ID', 'id'], (v) => String(v));
  assign('column_name', ['COLUMN_NAME', 'name'], (v) => String(v));
  assign('business_name', ['BUSINESS_NAME']);
  assign('description', ['DESCRIPTION']);
  assign('data_type', ['DATA_TYPE']);
  assign('semantic_role', ['SEMANTIC_ROLE']);
  assign('semantic_type', ['SEMANTIC_TYPE']);
  assign('usage_notes', ['USAGE_NOTES']);
  assign('business_rules', ['BUSINESS_RULES']);

  ['is_primary_key', 'nullable', 'required', 'sensitive'].forEach((flag) => {
    assign(flag, [flag.toUpperCase()], (value) => mapBoolean(value));
  });

  return decorated;
}

function decorateSemanticTable(record) {
  if (!record || typeof record !== 'object') {
    return record;
  }

  const decorated = { ...record };

  const assign = (canonical, aliases = [], transform) => {
    const value = coalesce(decorated[canonical], ...aliases.map((alias) => decorated[alias]));
    if (value === undefined) {
      return;
    }
    const finalValue = transform ? transform(value) : value;
    decorated[canonical] = finalValue;
    aliases.forEach((alias) => {
      decorated[alias] = finalValue;
    });
  };

  assign('table_id', ['TABLE_ID', 'id'], (v) => String(v));
  assign('semantic_database_id', ['semanticDatabaseId', 'SEMANTIC_DATABASE_ID']);
  assign('account_id', ['accountId', 'ACCOUNT_ID', 'ACCOUNTID']);
  assign('connection_id', ['connectionId', 'CONNECTION_ID']);
  assign('database', ['DATABASE', 'database_name', 'DATABASE_NAME']);
  assign('schema', ['SCHEMA', 'schema_name', 'SCHEMA_NAME']);
  assign('table_name', ['TABLE_NAME', 'name']);
  assign('catalog', ['CATALOG']);
  assign('display_name', ['displayName', 'DISPLAY_NAME']);
  assign('description', ['DESCRIPTION']);
  assign('sql_dialect', ['sqlDialect', 'SQL_DIALECT']);
  assign('tags', ['TAGS']);
  assign('is_active', ['IS_ACTIVE'], (value) => mapBoolean(value));
  assign('version', ['VERSION']);

  const softDelete = mapSoftDeletePayload(coalesce(decorated.soft_delete, decorated.softDelete, decorated.SOFT_DELETE));
  if (softDelete) {
    decorated.soft_delete = {
      deleted: softDelete.deleted ?? false,
      deleted_at: softDelete.deleted_at ?? null
    };
    decorated.softDelete = decorated.soft_delete;
    decorated.SOFT_DELETE = {
      DELETED: decorated.soft_delete.deleted ?? false,
      DELETED_AT: decorated.soft_delete.deleted_at ?? null
    };
  }

  if (Array.isArray(decorated.columns)) {
    decorated.columns = decorated.columns.map(decorateColumn);
  }
  if (Array.isArray(decorated.COLUMNS)) {
    decorated.COLUMNS = decorated.COLUMNS.map(decorateColumn);
  }

  return decorated;
}

function adaptSemanticTableResponse(data) {
  if (Array.isArray(data)) {
    return data.map(decorateSemanticTable);
  }

  if (data && typeof data === 'object') {
    const result = { ...data };

    if (Array.isArray(result.items)) {
      result.items = result.items.map(decorateSemanticTable);
    }

    if (Array.isArray(result.data)) {
      result.data = result.data.map(decorateSemanticTable);
    }

    if (Array.isArray(result.semantic_tables)) {
      result.semantic_tables = result.semantic_tables.map(decorateSemanticTable);
    }

    return decorateSemanticTable(result);
  }

  return data;
}

function normalizeSoftDeleteOptions(options) {
  if (options === undefined) {
    return { deleted: true };
  }

  if (typeof options === 'boolean') {
    return { deleted: options };
  }

  const body = mapSoftDeletePayload(options) || {};

  if (body.deleted === undefined) {
    body.deleted = true;
  }

  const deletedAt = coalesce(options.deleted_at, options.DELETED_AT);
  if (deletedAt !== undefined) {
    body.deleted_at = deletedAt;
  }

  return body;
}

//************ */
async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();
    //const accessToken = tokens?.accessToken?.toString();

    if (idToken) {
      return { Authorization: `Bearer ${idToken}` };
    }
    /*
    if (accessToken) {
      return { Authorization: `Bearer ${accessToken}` };
    }
    */
  } catch (error) {
    console.error('SemanticModelService.getAuthHeader: unable to fetch session', error);
  }

  return {};
}

async function fetchWithAuth(input, init = {}) {

  const authHeader = await getAuthHeader();

  const headers = {
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers || {}),
    //...defaultHeaders,
    ...authHeader
  };

  // SECURITY (fix MED-06, auditoría 2026-09-07): no loguear el Cognito ID
  // token en texto plano -- el header Authorization completo se logueaba
  // sin redactar en cada request.
  console.log('Fetching %s with headers: %o', JSON.stringify(input), JSON.stringify({ ...headers, ...(headers.Authorization ? { Authorization: '***' } : {}) }));

  const response = await fetch(input, {
    ...init,
    headers,
    mode: 'cors',
    cache: 'no-cache'
  });

  
  console.log('Response from %s: %o', JSON.stringify(input), response );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${errorBody || 'Unexpected error'}`);
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text || null;
  }
}

const SemanticTableService = {
  async list({ accountId, semanticDatabaseId, includeDeleted = false, limit, nextToken } = {}) {
    const qs = new URLSearchParams();

    if (accountId) {
      qs.set('account_id', String(accountId));
    }
    if (semanticDatabaseId) {
      qs.set('semantic_database_id', String(semanticDatabaseId));
    }
    if (includeDeleted) {
      qs.set('include_deleted', 'true');
    }
    if (limit !== undefined && limit !== null) {
      qs.set('limit', String(limit));
    }
    if (nextToken) {
      qs.set('next_token', nextToken);
    }

    const query = qs.toString();
    const url = query ? `${BASE_URL}?${query}` : BASE_URL;
    const response = await fetchWithAuth(url, { method: 'GET' });
    return adaptSemanticTableResponse(response);
  },

  // ---- Joins (relaciones entre tablas) ----------------------------------
  // Formato del join (el mismo que arma SemanticTableFormDialog y el
  // aprovisionamiento automático): { target_table (table_name destino),
  // target_schema?, target_database?, type: left|inner|right|full,
  // cardinality?, on: [{ source_column, target_column }], purpose? }.
  // El backend asigna join_id al crear y en el update mezcla los campos.
  async addJoin(tableId, join) {
    if (!tableId) {
      throw new Error('SemanticTableService.addJoin: tableId es requerido');
    }
    return fetchWithAuth(`${BASE_URL}/${encodeURIComponent(tableId)}/joins`, {
      method: 'POST',
      body: JSON.stringify(join)
    });
  },

  async updateJoin(tableId, joinId, join) {
    if (!tableId || !joinId) {
      throw new Error('SemanticTableService.updateJoin: tableId y joinId son requeridos');
    }
    return fetchWithAuth(`${BASE_URL}/${encodeURIComponent(tableId)}/joins/${encodeURIComponent(joinId)}`, {
      method: 'PUT',
      body: JSON.stringify(join)
    });
  },

  async deleteJoin(tableId, joinId) {
    if (!tableId || !joinId) {
      throw new Error('SemanticTableService.deleteJoin: tableId y joinId son requeridos');
    }
    return fetchWithAuth(`${BASE_URL}/${encodeURIComponent(tableId)}/joins/${encodeURIComponent(joinId)}`, {
      method: 'DELETE'
    });
  },

  async getById(tableId) {
    if (!tableId) {
      throw new Error('SemanticTableService.getById: tableId es requerido');
    }
    const response = await fetchWithAuth(`${BASE_URL}/${encodeURIComponent(tableId)}`, { method: 'GET' });
    return adaptSemanticTableResponse(response);
  },

  async create(payload) {
    const body = buildSemanticTablePayload(payload);
    const response = await fetchWithAuth(BASE_URL, {
      method: 'POST',
      body: JSON.stringify(body)
    });
    return adaptSemanticTableResponse(response);
  },

  async update(tableId, payload) {
    if (!tableId) {
      throw new Error('SemanticTableService.update: tableId es requerido');
    }

    const body = buildSemanticTablePayload({
      ...payload,
      table_id: payload?.table_id ?? tableId
    });

    const response = await fetchWithAuth(`${BASE_URL}/${encodeURIComponent(tableId)}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
    return adaptSemanticTableResponse(response);
  },

  async softDelete(tableId, options = {}) {
    if (!tableId) {
      throw new Error('SemanticTableService.softDelete: tableId es requerido');
    }

    const body = normalizeSoftDeleteOptions(options);
    const response = await fetchWithAuth(`${BASE_URL}/${encodeURIComponent(tableId)}/soft-delete`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
    return adaptSemanticTableResponse(response);
  },

  /**
   * Generate metadata for a database table.
   *
   * Two mutually exclusive modes, picked by which fields are present:
   * - "connection" (original): `connection_id` + `database` -- introspects
   *   a live SQL connection via DatabaseInspector on the backend.
   * - "columns" (2026-09-15): `columns` -- generates/regenerates the
   *   description for a table whose columns are already known (manual
   *   entity creation, or an existing entity's stored columns), with no
   *   live DB connection needed. Works for any origin, including
   *   Excel/CSV-ingested entities (Athena/Glue) that the "connection" mode
   *   can't introspect. See whisperbi-back-semantic-model's
   *   table_metadata_generator/app.py::_handle_columns_mode.
   *
   * @param {Object} payload
   * @param {string} payload.table_name - Table name
   * @param {string} [payload.connection_id] - UUID of the connection ("connection" mode)
   * @param {string} [payload.database] - Database name ("connection" mode)
   * @param {string} [payload.schema] - Schema name (optional, "connection" mode)
   * @param {Array<Object>} [payload.columns] - Known columns ("columns" mode) --
   *   [{column_name, data_type, is_primary_key, nullable}, ...]
   * @param {string} [payload.semantic_database_id] - Used server-side to resolve
   *   the linked workspace's business_area, if any ("columns" mode only)
   * @param {boolean} payload.generate_ai_descriptions - Enable AI generation
   * @param {string} payload.language - Language for AI descriptions (en, es, pt)
   * @param {boolean} payload.async_mode - "connection" mode only, defaults to true for AI
   * @returns {Promise<Object>} Generated metadata or job info
   */
  async generateMetadata(payload) {
    if (!payload || typeof payload !== 'object') {
      throw new Error('SemanticTableService.generateMetadata: payload es requerido');
    }

    if (!payload.table_name) {
      throw new Error('SemanticTableService.generateMetadata: table_name es requerido');
    }

    let body;

    if (payload.columns) {
      if (!Array.isArray(payload.columns) || payload.columns.length === 0) {
        throw new Error('SemanticTableService.generateMetadata: columns debe ser un array no vacío');
      }

      body = {
        table_name: String(payload.table_name),
        columns: payload.columns,
        generate_ai_descriptions: payload.generate_ai_descriptions !== false
      };

      if (payload.semantic_database_id) {
        body.semantic_database_id = String(payload.semantic_database_id);
      }

      if (payload.language) {
        body.language = String(payload.language);
      }
    } else {
      if (!payload.connection_id) {
        throw new Error('SemanticTableService.generateMetadata: connection_id es requerido');
      }

      if (!payload.database) {
        throw new Error('SemanticTableService.generateMetadata: database es requerido');
      }

      body = {
        connection_id: String(payload.connection_id),
        database: String(payload.database),
        table_name: String(payload.table_name),
        generate_ai_descriptions: payload.generate_ai_descriptions !== false
      };

      if (payload.schema) {
        body.schema = String(payload.schema);
      }

      if (payload.language) {
        body.language = String(payload.language);
      }

      // async_mode defaults to true for AI generation
      if (payload.async_mode !== undefined) {
        body.async_mode = Boolean(payload.async_mode);
      }
    }

    const response = await fetchWithAuth(`${API_BASE}/table-metadata/generate`, {
      method: 'POST',
      body: JSON.stringify(body)
    });

    // If response contains job_id, it's async mode
    if (response.job_id) {
      return response; // Return job info as-is
    }

    // Otherwise, it's sync mode - adapt the response
    return adaptSemanticTableResponse(response);
  },

  /**
   * Get job status for async metadata generation.
   * 
   * @param {string} jobId - Job ID returned from generateMetadata
   * @returns {Promise<Object>} Job status and result
   */
  async getJobStatus(jobId) {
    if (!jobId) {
      throw new Error('SemanticTableService.getJobStatus: jobId es requerido');
    }

    const response = await fetchWithAuth(`${API_BASE}/table-metadata/jobs/${jobId}`, {
      method: 'GET'
    });

    // If job is completed, adapt the result
    if (response.status === 'completed' && response.result) {
      return {
        ...response,
        result: adaptSemanticTableResponse(response.result)
      };
    }

    return response;
  },

  /**
   * Poll job status until completion or timeout.
   * 
   * @param {string} jobId - Job ID to poll
   * @param {Object} options - Polling options
   * @param {number} options.intervalMs - Polling interval in milliseconds (default: 3000)
   * @param {number} options.maxAttempts - Maximum polling attempts (default: 60)
   * @param {Function} options.onProgress - Callback for progress updates
   * @returns {Promise<Object>} Final job result
   */
  async pollJobStatus(jobId, options = {}) {
    const {
      intervalMs = 3000,
      maxAttempts = 60, // 3 minutes with 3-second intervals
      onProgress = null
    } = options;

    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const job = await this.getJobStatus(jobId);

        // Call progress callback if provided
        if (onProgress) {
          onProgress(job, attempts);
        }

        // Check job status
        if (job.status === 'completed') {
          return job.result;
        } else if (job.status === 'failed') {
          throw new Error(job.error_message || 'Metadata generation failed');
        }

        // Job is still pending or processing - wait and retry
        await new Promise(resolve => setTimeout(resolve, intervalMs));
        attempts++;

      } catch (error) {
        // If it's a job failure, throw immediately
        if (error.message && error.message !== 'Network error') {
          throw error;
        }

        // For network errors, retry
        console.warn(`Polling attempt ${attempts + 1} failed:`, error);
        await new Promise(resolve => setTimeout(resolve, intervalMs));
        attempts++;
      }
    }

    throw new Error('Job timeout: metadata generation took too long. Please try again later.');
  },

  /**
   * Generate metadata with automatic polling (convenience method).
   * 
   * @param {Object} payload - Same as generateMetadata
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<Object>} Generated metadata
   */
  async generateMetadataWithPolling(payload, onProgress = null) {
    // Start metadata generation
    const response = await this.generateMetadata(payload);

    // If it's async mode (has job_id), poll for results
    if (response.job_id) {
      return await this.pollJobStatus(response.job_id, { onProgress });
    }

    // Otherwise, return the result directly (sync mode)
    return response;
  },

  /**
   * List tables from a database connection.
   * 
   * @param {string} connectionId - UUID of the connection
   * @param {string} database - Database name
   * @param {string} schema - Schema name (optional)
   * @returns {Promise<Array>} List of tables
   */
  async listDatabaseTables(connectionId, database, schema = null) {
    if (!connectionId) {
      throw new Error('SemanticTableService.listDatabaseTables: connectionId es requerido');
    }

    if (!database) {
      throw new Error('SemanticTableService.listDatabaseTables: database es requerido');
    }

    const params = new URLSearchParams({
      connection_id: String(connectionId),
      database: String(database)
    });

    if (schema) {
      params.set('schema', String(schema));
    }

    const url = `${API_BASE}/table-metadata/tables?${params.toString()}`;
    const response = await fetchWithAuth(url, { method: 'GET' });

    if (response && Array.isArray(response.tables)) {
      return response.tables;
    }

    if (Array.isArray(response)) {
      return response;
    }

    return [];
  }
};

export default SemanticTableService;
